import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

export default function PaperTestsPage() {
  const [modules, setModules] = useState([]);
  const [name, setName] = useState("");
  const [copies, setCopies] = useState(1);
  const [employees, setEmployees] = useState([]);
  const [test, setTest] = useState(null);
  const [empId, setEmpId] = useState("");
  const [empQ, setEmpQ] = useState("");
  const [answers, setAnswers] = useState({});
  const [note, setNote] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/training/paper-tests-info").then((r) => setModules(r.data.modules)).catch((e) => toast.error(formatApiError(e)));
    api.get("/admin/training/electronic-test/employees").then((r) => setEmployees(r.data.employees)).catch(() => {});
  }, []);

  const download = async (path, filename) => {
    try {
      const r = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const params = (withCopies) => {
    const p = new URLSearchParams();
    if (withCopies) p.set("copies", String(Math.max(1, Math.min(20, Number(copies) || 1))));
    if (name.trim()) p.set("name", name.trim());
    const s = p.toString();
    return s ? `?${s}` : "";
  };

  const startElectronic = async (order) => {
    setBusy(true);
    setResult(null);
    try {
      const r = await api.get(`/admin/training/electronic-test/${order}`);
      setTest(r.data);
      setAnswers({});
      setEmpId("");
      setEmpQ("");
      setNote("");
      setTimeout(() => document.getElementById("electronic-test-panel")?.scrollIntoView({ behavior: "smooth" }), 50);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const submitElectronic = async () => {
    if (!empId) { toast.error("Pick the employee first"); return; }
    const arr = test.questions.map((_, i) => answers[i]);
    if (arr.some((a) => a === undefined)) { toast.error("Answer every question before submitting"); return; }
    setBusy(true);
    try {
      const r = await api.post("/admin/training/electronic-test/submit", { order: test.order, user_id: empId, answers: arr, note });
      setResult(r.data);
      if (r.data.passed) toast.success(`Passed — ${r.data.score}% filed to the employee record`);
      else toast.error(`Below passing — ${r.data.score}% (passing is ${r.data.passing_score}%)`);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const shownEmps = empQ.trim()
    ? employees.filter((e) => (e.name || "").toLowerCase().includes(empQ.trim().toLowerCase()))
    : employees;

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-4 sm:p-6" data-testid="paper-tests-page">
      <div>
        <h1 className="font-heading text-2xl font-semibold text-stone-900" data-testid="paper-tests-title">Paper & electronic tests</h1>
        <p className="mt-1 text-sm text-stone-500">
          Training competency tests for modules 1–7. Print paper copies, download a fillable PDF to type answers on a computer,
          or fill the test in on screen and file it straight to the employee's record. Answer keys print only on the office copy.
        </p>
      </div>

      <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="paper-tests-settings">
        <div className="flex flex-wrap items-end gap-4">
          <div className="min-w-56 flex-1">
            <label className="text-xs font-medium uppercase tracking-wide text-stone-500">Test-taker name (optional — printed on the test)</label>
            <Input data-testid="paper-test-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Jane Smith" className="mt-1" />
          </div>
          <div className="w-28">
            <label className="text-xs font-medium uppercase tracking-wide text-stone-500">Copies</label>
            <Input data-testid="paper-test-copies-input" type="number" min={1} max={20} value={copies} onChange={(e) => setCopies(e.target.value)} className="mt-1" />
          </div>
          <Button data-testid="paper-test-print-all" variant="outline" onClick={() => download(`/admin/training/paper-tests.pdf${params(true)}`, "training-tests-all.pdf")}>
            Print packet (all 7)
          </Button>
          <Button data-testid="paper-test-fillable-all" variant="outline" onClick={() => download(`/admin/training/paper-tests-fillable.pdf${params(false)}`, "training-tests-fillable-all.pdf")}>
            Fillable PDF (all 7)
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-stone-200 bg-white shadow-sm" data-testid="paper-tests-modules">
        <div className="border-b border-stone-100 px-5 py-3 text-sm font-semibold text-stone-800">Modules 1–7</div>
        {modules.map((m) => (
          <div key={m.order} className="flex flex-wrap items-center gap-3 border-b border-stone-100 px-5 py-3 last:border-0" data-testid={`paper-test-module-${m.order}`}>
            <div className="min-w-0 flex-1">
              <div className="font-medium text-stone-900">Module {m.order}: {m.title}</div>
              <div className="text-xs text-stone-500">{m.questions} questions · passing {m.passing_score}%</div>
            </div>
            <Button size="sm" variant="outline" data-testid={`paper-test-print-${m.order}`}
              onClick={() => download(`/admin/training/paper-tests.pdf?module_order=${m.order}&copies=${Math.max(1, Math.min(20, Number(copies) || 1))}${name.trim() ? `&name=${encodeURIComponent(name.trim())}` : ""}`, `training-test-module-${m.order}.pdf`)}>
              Paper
            </Button>
            <Button size="sm" variant="outline" data-testid={`paper-test-fillable-${m.order}`}
              onClick={() => download(`/admin/training/paper-tests-fillable.pdf?module_order=${m.order}${name.trim() ? `&name=${encodeURIComponent(name.trim())}` : ""}`, `training-test-fillable-module-${m.order}.pdf`)}>
              Fillable PDF
            </Button>
            <Button size="sm" data-testid={`paper-test-electronic-${m.order}`} disabled={busy} onClick={() => startElectronic(m.order)}>
              Electronic
            </Button>
          </div>
        ))}
        {modules.length === 0 && <div className="px-5 py-6 text-sm text-stone-500">Loading modules…</div>}
      </div>

      {test && (
        <div id="electronic-test-panel" className="rounded-lg border border-[#4A2340]/30 bg-white p-5 shadow-sm" data-testid="electronic-test-panel">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="font-heading text-lg font-semibold text-stone-900" data-testid="electronic-test-heading">
                Electronic test — Module {test.order}: {test.title}
              </h2>
              <p className="text-xs text-stone-500">{test.questions.length} questions · passing {test.passing}% · scores and files automatically on submit</p>
            </div>
            <Button size="sm" variant="ghost" data-testid="electronic-test-close" onClick={() => { setTest(null); setResult(null); }}>Close</Button>
          </div>

          {result ? (
            <div className={`mt-4 rounded-md border p-4 ${result.passed ? "border-[#2F6B4F]/30 bg-[#2F6B4F]/5" : "border-[#C0392B]/30 bg-[#C0392B]/5"}`} data-testid="electronic-test-result">
              <div className="text-lg font-semibold text-stone-900">{result.passed ? "Passed" : "Below passing"} — {result.score}% ({result.correct} of {result.total})</div>
              <p className="mt-1 text-sm text-stone-600">
                Filed to {employees.find((e) => e.id === empId)?.name || "the employee"}'s training record{result.passed ? " and counted as completed" : " (not passed — retake needed)"}.
              </p>
              <Button size="sm" variant="outline" className="mt-3" data-testid="electronic-test-another" onClick={() => { setResult(null); setAnswers({}); setEmpId(""); setNote(""); }}>
                Record another
              </Button>
            </div>
          ) : (
            <>
              <div className="mt-4">
                <label className="text-xs font-medium uppercase tracking-wide text-stone-500">Employee taking the test</label>
                <Input data-testid="electronic-test-employee-search" value={empQ} onChange={(e) => setEmpQ(e.target.value)} placeholder="Type to search staff…" className="mt-1" />
                <select data-testid="electronic-test-employee-select" className="mt-2 w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm"
                  value={empId} onChange={(e) => setEmpId(e.target.value)}>
                  <option value="">— choose employee —</option>
                  {shownEmps.map((e) => <option key={e.id} value={e.id}>{e.name}{e.employee_id ? ` (#${e.employee_id})` : ""}</option>)}
                </select>
              </div>

              <div className="mt-5 space-y-5">
                {test.questions.map((q, qi) => (
                  <div key={qi} data-testid={`electronic-test-question-${qi + 1}`}>
                    <div className="text-sm font-medium text-stone-900">{qi + 1}. {q.text}</div>
                    <div className="mt-2 space-y-1.5">
                      {(q.choices || []).map((ch, ci) => (
                        <label key={ci} className={`flex cursor-pointer items-start gap-2 rounded-md border px-3 py-2 text-sm ${answers[qi] === ci ? "border-[#4A2340] bg-[#4A2340]/5" : "border-stone-200"}`}
                          data-testid={`electronic-test-answer-${qi + 1}-${ci}`}>
                          <input type="radio" name={`q${qi}`} className="mt-0.5" checked={answers[qi] === ci}
                            onChange={() => setAnswers((a) => ({ ...a, [qi]: ci }))} />
                          <span>{String.fromCharCode(65 + ci)}. {ch}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-5">
                <label className="text-xs font-medium uppercase tracking-wide text-stone-500">Note (optional)</label>
                <Input data-testid="electronic-test-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. taken in office, verbal assist" className="mt-1" />
              </div>
              <Button className="mt-4" data-testid="electronic-test-submit" disabled={busy} onClick={submitElectronic}>
                Score & file to employee record
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
